import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Send, ArrowLeft, User, Loader2, MessageSquare } from "lucide-react";

export default function MessageThread({ applicationId, onBack }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const { user } = useAuth();

  const load = async () => {
    try {
      const res = await api.get(`/messages/thread/${applicationId}`);
      setData(res.data);
    } catch { toast.error("Could not load thread"); }
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [applicationId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.post(`/messages/${applicationId}`, { body });
      setText("");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  if (!data) return <div className="text-zinc-500 font-mono-alt uppercase text-sm p-4">Loading…</div>;

  return (
    <div className="border border-zinc-800 bg-zinc-950 flex flex-col h-[540px]">
      <div className="border-b border-zinc-800 p-4 flex items-center gap-3">
        {onBack && (
          <button data-testid="thread-back" onClick={onBack} className="text-zinc-500 hover:text-yellow-500"><ArrowLeft size={16} /></button>
        )}
        {data.apprentice?.picture ? <img src={data.apprentice.picture} alt="" className="w-9 h-9 border border-zinc-800" /> : <div className="w-9 h-9 border border-zinc-800 flex items-center justify-center"><User size={16} className="text-zinc-500" /></div>}
        <div className="flex-1">
          <div className="font-display uppercase text-lg leading-none">{data.apprentice?.name || "Applicant"}</div>
          <Link to={`/jobs/${data.job?.id}`} className="text-xs text-zinc-500 hover:text-yellow-500 font-mono-alt uppercase tracking-wider">RE: {data.job?.title}</Link>
        </div>
        <span className={`text-[10px] uppercase font-bold tracking-widest border px-2 py-0.5 ${statusCls(data.application?.status)}`}>{data.application?.status}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {(data.messages || []).length === 0 ? (
          <div className="text-center text-zinc-500 py-8 text-sm">
            <MessageSquare className="mx-auto mb-2 text-yellow-500" size={24} />
            No messages yet — say hi!
          </div>
        ) : (
          data.messages.map((m) => {
            const mine = m.sender_user_id === user?.user_id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] p-3 border ${mine ? "bg-yellow-500/10 border-yellow-500/40 text-white" : "bg-zinc-900 border-zinc-800 text-zinc-200"}`}>
                  <div className="text-[10px] font-mono-alt uppercase tracking-widest text-zinc-500 mb-1">
                    {m.sender_role === "employer" ? "Employer" : "Apprentice"} &middot; {new Date(m.created_at).toLocaleString()}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="border-t border-zinc-800 p-3 flex gap-2">
        <input
          data-testid="thread-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 bg-zinc-900 border border-zinc-800 focus:border-yellow-500 px-3 py-2 text-white placeholder:text-zinc-600 focus:outline-none"
        />
        <button data-testid="thread-send" disabled={sending || !text.trim()} className="bg-yellow-500 text-black font-bold uppercase tracking-wider px-4 hover:bg-yellow-400 disabled:opacity-50 flex items-center gap-2">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
          Send
        </button>
      </form>
    </div>
  );
}

function statusCls(s) {
  const m = { applied: "text-blue-400 border-blue-400/40", interviewing: "text-yellow-500 border-yellow-500/40", offer: "text-green-500 border-green-500/40", rejected: "text-red-500 border-red-500/40", withdrawn: "text-zinc-500 border-zinc-500/40" };
  return m[s] || "text-zinc-500 border-zinc-700";
}
